import { GitHubAuthError, GitHubConflictError, GitHubError, GitHubNotFoundError, } from "./errors.js";
import { decodeBase64Utf8, encodeBase64Utf8 } from "./base64.js";
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
export class GitHubClient {
    owner;
    repo;
    token;
    branch;
    fetchImpl;
    baseUrl;
    constructor(opts) {
        this.owner = opts.owner;
        this.repo = opts.repo;
        this.token = opts.token;
        this.branch = opts.branch ?? "main";
        this.fetchImpl =
            opts.fetch ??
                ((input, init) => {
                    if (typeof globalThis.fetch !== "function") {
                        throw new Error("fetch is not available in this environment; pass opts.fetch");
                    }
                    return globalThis.fetch(input, init);
                });
        this.baseUrl = opts.baseUrl ?? "https://api.github.com";
    }
    headers(extra = {}) {
        return {
            Accept: "application/vnd.github+json",
            ...extra,
            Authorization: `Bearer ${this.token}`,
            "X-GitHub-Api-Version": "2022-11-28",
        };
    }
    contentsUrl(path) {
        const enc = path.split("/").map(encodeURIComponent).join("/");
        const o = encodeURIComponent(this.owner);
        const r = encodeURIComponent(this.repo);
        return `${this.baseUrl}/repos/${o}/${r}/contents/${enc}?ref=${encodeURIComponent(this.branch)}`;
    }
    throwForStatus(res, path) {
        if (res.status === 401)
            throw new GitHubAuthError();
        if (res.status === 404)
            throw new GitHubNotFoundError(path);
        if (!res.ok) {
            throw new GitHubError(`GitHub ${res.status} on ${path}`, res.status);
        }
    }
    async read(path) {
        const res = await this.fetchImpl(this.contentsUrl(path), {
            method: "GET",
            headers: this.headers(),
        });
        this.throwForStatus(res, path);
        return this.parseRead(res);
    }
    async readIfChanged(path, etag) {
        const res = await this.fetchImpl(this.contentsUrl(path), {
            method: "GET",
            headers: this.headers({ "If-None-Match": etag }),
        });
        if (res.status === 304)
            return null;
        this.throwForStatus(res, path);
        return this.parseRead(res);
    }
    async parseRead(res) {
        const body = (await res.json());
        if (typeof body.content !== "string" || body.encoding !== "base64") {
            throw new GitHubError(`unexpected GitHub contents payload (encoding=${body.encoding})`, res.status);
        }
        const decoded = decodeBase64Utf8(body.content);
        const data = JSON.parse(decoded);
        return {
            data,
            sha: body.sha,
            etag: res.headers.get("etag") ?? "",
        };
    }
    async write(path, data, message, opts = {}) {
        const content = encodeBase64Utf8(JSON.stringify(data, null, 2));
        const body = {
            message,
            content,
            branch: this.branch,
        };
        if (opts.prevSha)
            body.sha = opts.prevSha;
        const res = await this.fetchImpl(this.contentsUrl(path), {
            method: "PUT",
            headers: this.headers({ "Content-Type": "application/json" }),
            body: JSON.stringify(body),
        });
        if (res.status === 409 || res.status === 422) {
            throw new GitHubConflictError(path, res.status);
        }
        if (res.status === 401)
            throw new GitHubAuthError();
        if (!res.ok) {
            throw new GitHubError(`GitHub ${res.status} on PUT ${path}`, res.status);
        }
        const respBody = (await res.json());
        return {
            sha: respBody.content.sha,
            etag: res.headers.get("etag") ?? "",
        };
    }
    /**
     * Read → mutate → write with optimistic concurrency.
     *
     * The `mutate` function MUST be pure: it receives the latest server-side
     * data and returns the next value. On a 409 (someone else wrote first),
     * the client re-reads and calls `mutate` again against the fresh data —
     * which is only safe if `mutate` does not close over stale state.
     */
    async update(path, mutate, message, opts = {}) {
        const maxAttempts = opts.maxAttempts ?? 3;
        const baseDelayMs = opts.baseDelayMs ?? 200;
        let lastConflict = null;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const current = await this.read(path);
            const next = mutate(current.data);
            try {
                const written = await this.write(path, next, message, {
                    prevSha: current.sha,
                });
                return { data: next, sha: written.sha, etag: written.etag };
            }
            catch (err) {
                if (!(err instanceof GitHubConflictError))
                    throw err;
                lastConflict = err;
                if (attempt < maxAttempts - 1) {
                    await sleep(baseDelayMs * 2 ** attempt);
                }
            }
        }
        throw lastConflict ?? new GitHubConflictError(path);
    }
}
//# sourceMappingURL=client.js.map